/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { TransitionProps } from "notistack";
import { useNotification } from "../../context/notification-provider";
import { updateScoringRule } from "./service";
import { ScoringRule } from "./types";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface EditScoringRuleDialogProps {
  open: boolean;
  onClose: () => void;
  scoringRule: ScoringRule | null;
}

const EditScoringRuleDialog: React.FC<EditScoringRuleDialogProps> = ({
  open,
  onClose,
  scoringRule,
}) => {
  const [score, setScore] = React.useState(0);
  const [enabled, setEnabled] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { showNotification } = useNotification();

  React.useEffect(() => {
    if (scoringRule) {
      setScore(scoringRule.score);
      setEnabled(scoringRule.enabled);
    }
  }, [scoringRule]);

  const handleSubmit = async () => {
    if (!scoringRule) return;

    setIsSubmitting(true);
    try {
      await updateScoringRule({
        ...scoringRule,
        score,
        enabled,
      });
      showNotification({
        message: "Scoring rule updated successfully.",
        severity: "success",
        duration: 3000,
      });
      onClose();
    } catch (err) {
      showNotification({
        message: `Failed to update scoring rule. Reason: ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!scoringRule) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slots={{ transition: Transition }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Scoring Rule
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Rule Type:</strong> {scoringRule.ruleType}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Description:</strong> {scoringRule.description}
          </Typography>
        </Box>

        <TextField
          label="Score"
          type="number"
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label="Enabled"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          Update Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScoringRuleDialog;
